import { expValEquals } from '@confluence/feature-experiments';

import { createHeadingLink } from '@confluence/editor-heading-links/entry-points/createHeadingLink.ts';

export type SyncedBlockHeadingAnchorLinks = {
	allowNestedHeaderLinks: boolean;
	getHeadingLink: (headingId: string) => string;
};

/**
 * Heading anchor link config for headings rendered inside reference synced
 * blocks in the editor.
 *
 * Gated on the synced-block ToC experiment. When enabled, the renderer stamps
 * heading ids (required for Table-of-Contents deep links) and shows the heading
 * copy-link button. When it returns `undefined`, the renderer disables heading
 * ids entirely — which is the historical behaviour.
 *
 * Unlike the view-page renderer, `getHeadingLink` must be supplied here: the
 * platform default derives the link from `window.location`, which in the editor
 * is the *edit* URL. `getHeadingLinkPath` resolves to the view page path, so
 * copying a heading link from the editor yields the same view URL a reader gets.
 */
export const getSyncedBlockHeadingAnchorLinks = (
	getHeadingLinkPath: (() => string | null) | undefined,
): SyncedBlockHeadingAnchorLinks | undefined =>
	expValEquals('confluence_toc_synced_block_exp', 'isEnabled', true)
		? {
				allowNestedHeaderLinks: true,
				// `createHeadingLink` returns undefined when no path is resolvable (e.g.
				// during SSR). Fall back to '' so the renderer uses its own default
				// rather than rendering an `undefined` href.
				getHeadingLink: (headingId: string) =>
					createHeadingLink(getHeadingLinkPath, headingId) ?? '',
			}
		: undefined;
